import React, { useEffect, useState } from 'react'
import { useOktaAuth } from '@okta/okta-react'
import { toRelativeUrl } from '@okta/okta-auth-js'
import Loader from 'ui/components/Loader'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { BASE_URL } from 'helpers/constants'
import {
  setOktaUser,
  setAppUser,
  setIsLoggedIn,
  setFetchProgramApicall,
} from 'store/slices/userDetailSlice'
import useApiRequest from 'ui/utilities/useApiRequest'
import { showErrorToast } from './Toast/toast'
import AccessErrorPage from './AccessErrorPage'

const { Outlet, useNavigate } = require('react-router')

function PrivateOutlet() {
  const navigate = useNavigate()
  const { getData } = useApiRequest()
  const { oktaAuth, authState } = useOktaAuth()
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [newAuthState, setNewAuthState] = useState(authState)
  const effectiveAuthState = newAuthState !== null ? newAuthState : authState
  const userDetails = useSelector((state) => state.userDetails)
  const [queryParams] = useSearchParams()
  const [accessError, setAccessError] = useState(false)

  const getUserInfoFromOkta = async () => {
    const nodeEnv = (window._env_?.NODE_ENV || process.env?.NODE_ENV)
    const yourOktaDomain =
       (window._env_?.REACT_APP_USER_BY_EMAIL_OKTA_DOMAIN || process.env?.REACT_APP_USER_BY_EMAIL_OKTA_DOMAIN) ||
      (nodeEnv === 'production'
        ? 'https://secure.pepsico.com/oauth2/default/v1/userinfo'
        : 'https://secure.ite.pepsico.com/oauth2/default/v1/userinfo')
    try {
      const data = await getData(`${yourOktaDomain}`)
      dispatch(setOktaUser(data))
      const user = await getData(`${BASE_URL}/program/user/userByEmail?email=${data?.email}`)
      // getData swallows request errors and resolves with an { status, message } shape instead of
      // throwing, so a missing/deactivated/no-role user must be detected from the payload itself.
      if (!user?.id) {
        // showErrorToast(user?.message || 'User not found')
        setAccessError(true)
        dispatch(setIsLoggedIn(false))
        dispatch(setAppUser(null))
        return null
      }
      sessionStorage.setItem('app_user_details', JSON.stringify(user))
      dispatch(setIsLoggedIn(true))
      dispatch(setAppUser(user))
      dispatch(setFetchProgramApicall(true))
      return user
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('getUserInfoFromOkta', error)
      showErrorToast('User not found')
      dispatch(setIsLoggedIn(false))
      dispatch(setAppUser(null))
      return null
    }
  }

  // Below is the Okta Code
  useEffect(() => {
    const checkAndRefreshToken = async () => {
      try {
        if (authState?.isAuthenticated) {
          const { accessToken } = authState
          const currentTime = Math.floor(Date.now() / 1000)

          if (accessToken.expiresAt - currentTime < 60 && !isRefreshing) {
            setIsRefreshing(true)
            const { refreshToken, idToken } = JSON.parse(
              sessionStorage.getItem('okta-token-storage')
            )

            const tokenUrls = `${refreshToken.tokenUrl}`
            const clientId = `${idToken.clientId}`

            const body = new URLSearchParams()
            body.append('grant_type', 'refresh_token')
            body.append('client_id', clientId)
            body.append(
              'refresh_token',
              refreshToken.refreshToken ? refreshToken.refreshToken : refreshToken
            )
            body.append('scope', 'openid offline_access email profile')

            const response = await fetch(tokenUrls, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
              },
              body: body.toString(),
            })

            if (!response.ok) {
              throw new Error('Failed to refresh the token')
            }

            const data = await response.json()
            // eslint-disable-next-line camelcase
            const { access_token, refresh_token, expires_in } = data

            const newData = await oktaAuth.authStateManager.updateAuthState()

            // eslint-disable-next-line camelcase
            newData.refreshToken.refreshToken = refresh_token
            // eslint-disable-next-line camelcase
            const expiresIn = expires_in
            newData.refreshToken.expiresAt = Math.floor(Date.now() / 1000) + expiresIn

            sessionStorage.setItem('auth_token', access_token)
            sessionStorage.setItem('okta-token-storage', JSON.stringify(newData))
            sessionStorage.setItem('auth_state', JSON.stringify(newData))

            setNewAuthState(newData)
          }
        }
      } catch (error) {
        console.error('Error refreshing access token:', error)
        navigate('/login')
      } finally {
        setIsRefreshing(false)
      }
    }

    const interval = setInterval(() => {
      checkAndRefreshToken()
    }, 600000)

    return () => clearInterval(interval)
  }, [oktaAuth, authState, isRefreshing, navigate])

  useEffect(() => {
    setIsLoading(true)
    const checkAuth = async () => {
      if (!effectiveAuthState) {
        return
      }

      if (!effectiveAuthState?.isAuthenticated) {
        const originalUri = toRelativeUrl(window.location.href, window.location.origin)
        oktaAuth.setOriginalUri(originalUri)
        const external = queryParams.get('external')
        if (external === null) {
          navigate('/login')
        } else {
          await oktaAuth.signInWithRedirect()
        }
      } else if (authState?.isAuthenticated) {
        sessionStorage.setItem('auth_token', authState?.accessToken?.accessToken)
        if (!userDetails?.isLoggedIn || !userDetails?.appUser) {
          await getUserInfoFromOkta()
        }
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [oktaAuth, authState, navigate, dispatch, effectiveAuthState])

  if (accessError) {
    return <AccessErrorPage />
  }

  if (
    !effectiveAuthState ||
    !effectiveAuthState?.isAuthenticated ||
    !userDetails?.isLoggedIn ||
    !userDetails?.appUser
  ) {
    return <Loader isLoading={isLoading} />
  }

  return <Outlet />
}

export default PrivateOutlet
